import * as ngCore from '@angular/core'
import * as ents from '@attain-apps/entities'
import { Word } from '@attain-apps/string-builder'
import * as chartJs from 'chart.js'
import {
  ArcSlice,
  VennDiagramChart,
  VennDiagramController,
} from 'chartjs-chart-venn'
import * as rxjs from 'rxjs'

import * as transformers from './transformers'

@ngCore.Component({
  selector: 'atn-ds-chart-js-chart',
  standalone: true,
  styleUrls: ['./component.scss'],
  templateUrl: './component.html',
})
export class Component<
    TType extends Exclude<
      ents.core.display.chart.Type,
      // Types not supported by chart.js
      ents.core.display.chart.Type.Sunburst
    >,
  >
  implements ngCore.AfterViewInit, ngCore.OnDestroy
{
  /** Flag indicating whether Chart.js library has been initialized. */
  private static _initialized = false

  /**
   * Initializes the Chart.js library for the all charts.
   * This method ensures that Chart.js setup is executed only once during the application's runtime.
   * If not already initialized, it registers required Chart.js controllers and elements
   * and sets the `_initialized` flag to prevent redundant initialization.
   */
  private static _init(): void {
    if (!this._initialized) {
      // NOTE: If there is performance issue, only for used chart should be
      // registered controllers
      chartJs.Chart.register(...chartJs.registerables)
      chartJs.Chart.register(VennDiagramChart, VennDiagramController, ArcSlice)
      this._initialized = true
    }
  }

  /**
   * Property that holds a reference to a chart instance.
   */
  private readonly _chartRefSg: ngCore.WritableSignal<
    chartJs.Chart | undefined
  > = ngCore.signal(undefined)

  /**
   * Identifies the canvas HTML element used to render this chart.
   */
  // TODO: maybe using ViewChild to get the element directly would be a better
  // approach?
  public readonly id: string = Word.generate()

  /**
   * Chart input property that represents the chart configuration. This property
   * is required and must be provided by the parent component.
   */
  public chartSg = ngCore.input.required<
    chartJs.ChartConfiguration,
    ents.core.display.chart.Chart<TType>
  >({
    alias: 'chart',
    transform: transformers.toChartJs,
  })

  constructor() {
    Component._init()

    /**
     * An effect that updates the chart instance with the transformed chart data.
     * This effect is triggered whenever the `chartSg` signal changes.
     */
    ngCore.effect(() => {
      const chart = this.chartSg()
      const chartRef = this._chartRefSg()

      if (chart && chartRef) {
        chartRef.data = chart.data
        if (chart.options) {
          chartRef.options = chart.options
        }
        chartRef.update()
      }
    })
  }

  /** @inheritdoc */
  ngAfterViewInit(): void {
    // The chart currently attempts to render itself immediately after the view
    // is initialized. However, the parent components may not have completed
    // their width and height calculations by that time, which can cause the
    // chart to overflow its parent container instead of properly adjusting to
    // its available width. This workaround ensures that the chart waits for the
    // parent's layout to finalize before rendering, preventing overflow issues.
    rxjs
      .timer(0)
      .pipe(rxjs.first())
      .subscribe(() =>
        this._chartRefSg.set(new chartJs.Chart(this.id, this.chartSg())),
      )
  }

  /** @inheritdoc */
  ngOnDestroy(): void {
    // NOTE: we want to clear chart reference to prevent memory leaks every time
    // the component is destroyed
    const chart = this._chartRefSg()
    chart?.destroy()
  }
}
